import { useEffect, useRef, useState } from 'react'

import { useTimeEntry } from '@/components/time-entry'
import { isMainWindow, isTauri, setTrayTitle } from '@/lib/utils/tauri'

/**
 * Formats elapsed seconds as the compact `H:MM` the menu bar shows
 * (e.g. 42 minutes -> "0:42", 95 minutes -> "1:35").
 */
function formatTrayElapsed(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  return `${hours}:${String(minutes).padStart(2, '0')}`
}

/**
 * Desktop-only: mirrors the running timer into the macOS tray title.
 *
 * The provider re-computes `elapsed` every second, but the `H:MM` label only
 * changes when the minute rolls over, so the ref guard means we cross the IPC
 * boundary at most once a minute (plus once on start and once on stop).
 */
export function TrayTimerController() {
  const { activeEntry } = useTimeEntry()
  const [isPrimaryWindow, setIsPrimaryWindow] = useState(false)
  const lastTitleRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void isMainWindow().then(isMain => {
      if (!cancelled) setIsPrimaryWindow(isMain)
    })

    return () => {
      cancelled = true
    }
  }, [])

  const title = activeEntry ? formatTrayElapsed(activeEntry.elapsed) : ''

  useEffect(() => {
    if (!isTauri() || !isPrimaryWindow) return
    if (lastTitleRef.current === title) return

    lastTitleRef.current = title
    void setTrayTitle(title)
  }, [title, isPrimaryWindow])

  // Leaving the app shell (sign-out, window teardown) must not strand a stale
  // timer next to the tray icon.
  useEffect(() => {
    if (!isTauri() || !isPrimaryWindow) return

    return () => {
      lastTitleRef.current = null
      void setTrayTitle(null)
    }
  }, [isPrimaryWindow])

  return null
}
